'use client';

import { createContext, ReactNode, useContext, useState, useEffect } from 'react';
import { AlertColor } from '@mui/material/Alert';
import Toast from '@/components/Toast';

interface ToastData {
	message: string;
	linkText?: string;
	linkUrl?: string;
	severity: AlertColor;
}

interface ToastContextType {
	showToast: (data: ToastData) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
	const [toastQueue, setToastQueue] = useState<ToastData[]>([]);
	const [currentToast, setCurrentToast] = useState<ToastData | null>(null);

	const showToast = ({ message, linkText, linkUrl, severity }: ToastData) => {
		setToastQueue((prevQueue) => [...prevQueue, { message, linkText, linkUrl, severity }]);
	};

	useEffect(() => {
		if (!currentToast && toastQueue.length > 0) {
			setCurrentToast(toastQueue[0]);
			setToastQueue((prevQueue) => prevQueue.slice(1));
		}
	}, [toastQueue, currentToast]);

	const handleToastClose = () => {
		setCurrentToast(null);
	};

	return (
		<ToastContext.Provider value={{ showToast }}>
			{children}
			{currentToast && (
				<Toast
					message={currentToast.message}
					severity={currentToast.severity}
					linkText={currentToast.linkText}
					linkUrl={currentToast.linkUrl}
					open={!!currentToast}
					onClose={handleToastClose}
				/>
			)}
		</ToastContext.Provider>
	);
};

export const useToast = (): ToastContextType => {
	const context = useContext(ToastContext);
	if (context === undefined) {
		throw new Error('useToast must be used within a ToastProvider');
	}
	return context;
};
